export type { RebalancePlanAdviceCardDisplay } from '../../types/adviceCards.types'
import type { EliteInsights } from '../../../../types/misc.types'
import { roundOffAndFormatNumberWithCurrency } from '../../../../utils/utils'
import { buildRebalanceInsightDisplay } from '../../../new-elite-landing/rebalanceInsightDisplay'
import type { AdviceCardStat, RebalancePlanAdviceCardDisplay } from '../../types/adviceCards.types'

function calendarDaysTillTimestamp(timestamp: number): number {
  const now = new Date()
  const target = new Date(timestamp)
  now.setHours(0, 0, 0, 0)
  target.setHours(0, 0, 0, 0)
  return Math.round((target.getTime() - now.getTime()) / (24 * 60 * 60 * 1000))
}

function isMultiplePlansRecent(eliteInsights: EliteInsights): boolean {
  if (eliteInsights.rebalancePlanCount == null || eliteInsights.rebalanceSnapshotDate == null) {
    return false
  }
  const daysUntilSnapshot = calendarDaysTillTimestamp(eliteInsights.rebalanceSnapshotDate)
  return daysUntilSnapshot > -30 && daysUntilSnapshot <= 0
}

function formatFundCountValue(count: number): string {
  return `${count} ${count === 1 ? 'fund' : 'funds'}`
}

function appendMissedGainsStat(stats: AdviceCardStat[], missedGains?: number): AdviceCardStat[] {
  if (missedGains == null || missedGains <= 0) {
    return stats
  }

  return [
    ...stats,
    {
      value: roundOffAndFormatNumberWithCurrency(missedGains),
      label: 'missed gains'
    }
  ]
}

export function buildRebalancePlanAdviceCardDisplay(
  eliteInsights: EliteInsights | null | undefined,
  options?: {
    missedGains?: number
    fundsToRebalanceFallback?: number
  }
): RebalancePlanAdviceCardDisplay {
  const missedGains = options?.missedGains ?? 0
  const fundsToRebalanceFallback = options?.fundsToRebalanceFallback ?? 0

  if (!eliteInsights?.userEligibleForRebalance) {
    return { stats: [], insightTone: 'orange' }
  }

  const isRecentPlan = isMultiplePlansRecent(eliteInsights)
  const fundsToRebalance = eliteInsights.numberOfFundsToRebalance ?? fundsToRebalanceFallback ?? 0
  const fundsExcluded = eliteInsights.numberOfFundsExcludedFromRebalance ?? 0
  const hasRebalanceFunds = fundsToRebalance > 0
  const hasExcludedFunds = fundsExcluded > 0

  if (eliteInsights.rebalanceAvailable) {
    if (hasRebalanceFunds && hasExcludedFunds) {
      return {
        statusLabel: isRecentPlan ? 'New plan available' : undefined,
        stats: appendMissedGainsStat(
          [
            { value: formatFundCountValue(fundsToRebalance), label: 'to rebalance' },
            { value: formatFundCountValue(fundsExcluded), label: 'in review' }
          ],
          missedGains
        ),
        insightTone: 'orange'
      }
    }

    if (hasRebalanceFunds) {
      return {
        statusLabel: isRecentPlan ? 'New plan available' : undefined,
        stats: appendMissedGainsStat(
          [{ value: formatFundCountValue(fundsToRebalance), label: 'to rebalance' }],
          missedGains
        ),
        insightTone: 'orange'
      }
    }

    if (hasExcludedFunds) {
      return {
        stats: appendMissedGainsStat(
          [{ value: formatFundCountValue(fundsExcluded), label: 'in review' }],
          missedGains
        ),
        insightTone: 'orange'
      }
    }

    if (eliteInsights.userElite) {
      return {
        stats: appendMissedGainsStat([{ value: 'No funds', label: 'to rebalance' }], missedGains),
        insightTone: 'orange'
      }
    }
  } else if (eliteInsights.userElite) {
    const insight = buildRebalanceInsightDisplay(eliteInsights, fundsToRebalanceFallback)
    if (insight?.text) {
      return {
        stats: [{ label: insight.text, emphasize: true }],
        insightTone: insight.tone
      }
    }
  }

  return { stats: [], insightTone: 'orange' }
}
